<template>
  <div id="shelf" class="scroll-dest">
    <div class="grey-background content-container category-grid shelf-section">
      <!-- error state -->
      <callout v-if="error" type="error" :closable="false">
        <div>{{ error }}</div>
      </callout>

      <div v-if="hasResults || loading" class="shelf-center">
        <div ref="shelfResults" class="shelf-results">
          <!-- loading -->
          <skeleton-shelf-cards-loader v-if="loading" :cards="5" :accessible="false" />

          <div v-for="shelfCard in shelfCards" :key="shelfCard.id" class="shelf-card">
            <plan-card
              v-if="shelfCard.shelvable_type === 'plan'"
              :plan="shelfCard.shelvable"
              detail-view="myPrograms"
            />
          </div>
        </div>
      </div>

      <div v-if="noResults" class="shelf-no-results">
        <h3 class="heading -medium demi">Empty Shelf</h3>
        <p>
          Use the
          <i class="material-symbols-outlined inline" aria-label="bookmark icon">bookmark_add</i> to
          add items to the shelf. They will stay on the shelf regardless of search filters.
        </p>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue'
import { mapState, mapActions } from 'pinia'
import { useUserAuthStore } from '@/stores/user-auth'
import { useShelvesStore } from '@/stores/shelves'
import { fbApi, objToSearchParams } from '@/utils/http-api'
import Callout from '../notifications/callout.vue'
import PlanCard from '@/components/plans/plan-card.vue'
import SkeletonShelfCardsLoader from '@/components/loaders/skeleton-shelf-cards-loader.vue'
import type { ShelfCard } from '@/types/shelf-types'

/**
 * Custom expander that shows user's bookmarked items on their shelf
 * Custom expander that shows user's bookmarked items on their shelf
 */
export default defineComponent({
  name: 'Shelf',
  components: { SkeletonShelfCardsLoader, PlanCard, Callout },
  props: {
    entityType: {
      type: String,
      required: true,
      validator: (val: string) => ['plan'].includes(val),
    },
  },
  data() {
    return {
      loading: false,
      error: '',
    }
  },
  computed: {
    ...mapState(useUserAuthStore, ['cdn']),
    ...mapState(useShelvesStore, ['shelfCards']),

    hasResults() {
      return (
        !this.loading &&
        !this.error &&
        Array.isArray(this.shelfCards) &&
        this.shelfCards.length >= 1
      )
    },

    noResults() {
      return (
        !this.loading &&
        !this.error &&
        Array.isArray(this.shelfCards) &&
        this.shelfCards.length === 0
      )
    },
  },

  watch: {
    // if shelf item added, scroll to front of list
    shelfCards(newVal: ShelfCard[], oldVal: ShelfCard[]) {
      const refsShelfResults = this.$refs.shelfResults as HTMLDivElement

      if (newVal.length > oldVal.length && this.$refs && refsShelfResults) {
        this.$nextTick(() => {
          refsShelfResults.scrollTo({
            left: 0,
            behavior: 'smooth',
          })
        })
      }
    },
  },

  /* lifecycle */
  // kick off XHR when component is created
  // note if using v-if a parent component, this will fire each time
  created() {
    this.fetchShelfCards()
  },

  methods: {
    ...mapActions(useShelvesStore, ['setShelfCards']),

    async fetchShelfCards() {
      this.loading = true

      try {
        // do not show error modal
        const res = await fbApi.get('/shelves', {
          // do not show error modal on 5xx as page will still work
          // inline error message is shown
          skipErrorModal: true,
          searchParams: objToSearchParams({
            shelvableType: this.entityType,
          }),
        })

        if (!res || !res.body || !res.body.data || !Array.isArray(res.body.data)) {
          throw new Error(`Unable to fetch shelf items`)
        }

        // store in pinia so other components can add / edit
        this.setShelfCards(res.body.data)
      } catch (err) {
        console.error(err)
        this.error = 'Unable to fetch shelf items, please try again'
      } finally {
        this.loading = false
      }
    },
  },
})
</script>
